import {
  createContext,
  useContext,
  useState,
  ReactElement,
  useEffect,
} from "react";

import { useBreakpointRange } from "@newsela/angelou";

import { AgathaSchemaParamsEnum } from "@/search/NewSearchPageMVP/types/IParametersSchema";
import { useSearchPageContext } from "@/search/NewSearchPageMVP/provider/SearchPageProvider";
import {
  controlRemoveTaxonomyTag,
  initialContextValue,
} from "./TaxonomyTags.constants";
import {
  ISelectionState,
  ITaxonomyCategorizedData,
  ITaxonomyTags,
  ITopicsCheckbox,
} from "./TaxonomyTags.types";
import {
  isSelected,
  isSemiSelected,
  updateSelectionState,
} from "./utils/checkboxSelections";
import {
  buildSelectedTags,
  buildTaxonomyTags,
  collectTaxonomySlugs,
  flattenAndCategorizeData,
  getIdsFromDifferenceInQuery,
  getTop5ELA,
  getUniqueElements,
} from "./utils/processCategoryData";

interface ITaxonomyTagsProvider {
  children: ReactElement;
}

export interface ITaxonomyTagsContextProps {
  selectionState: ISelectionState;
  toggleSelection: (
    id: string | string[],
    shouldUpdateLatestSavedSelectionState?: boolean,
  ) => void;
  categorizedData: ITaxonomyCategorizedData;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  top5Ela: ITopicsCheckbox[];
  isChecked: (id: string, useSemiSelected?: boolean) => boolean;
  handleTopicSubmit: (
    currentSelectionState?: ISelectionState,
    shouldUpdate?: boolean,
  ) => ITaxonomyTags;
  setSelectionState: (selectionState: ISelectionState) => void;
  isTabletOrMobileView: boolean;
  prevTaxonomyQueryValue: string[];
  setPrevTaxonomyQueryValue: (prevTaxonomyQueryValue: string[]) => void;
  setShouldUpdateTags: (shouldUpdateTags: boolean) => void;
  latestSavedSelectionState: ISelectionState | null;
  setLatestSavedSelectionState: (
    lastSavedSelectionState: ISelectionState | null,
  ) => void;
}

const TaxonomyTagsContext =
  createContext<ITaxonomyTagsContextProps>(initialContextValue);

export const TaxonomyTagsProvider = ({ children }: ITaxonomyTagsProvider) => {
  const {
    getters: { taxonomyResults, taxonomyTagsSlugs, selectedTaxonomyTags },
    handlers: {
      setSelectedTaxonomyTags,
      hasSearchParamValue,
      deleteSearchParamValues,
      setTaxonomyTagsSlugs,
      setSearchParamValue,
    },
  } = useSearchPageContext();

  const MD_WIDTH = 1024;
  const currentBreakpointRange = useBreakpointRange([
    [0, MD_WIDTH],
    [MD_WIDTH + 1, Infinity],
  ]);

  const [selectionState, setSelectionState] = useState<ISelectionState>({});
  const [searchQuery, setSearchQuery] = useState("");
  const [prevTaxonomyQueryValue, setPrevTaxonomyQueryValue] = useState<
    string[]
  >([]);
  const [shouldUpdateTags, setShouldUpdateTags] = useState(false);
  const [latestSavedSelectionState, setLatestSavedSelectionState] =
    useState<ISelectionState | null>(null);
  const { categorizedData } = flattenAndCategorizeData(taxonomyResults);

  const top5Ela = getTop5ELA(categorizedData);

  const toggleSelection = (
    ids: string | string[],
    shouldUpdateLatestSavedSelectionState = true,
  ) => {
    const idArray = Array.isArray(ids) ? ids : [ids];
    let newSelectionState = { ...selectionState };
    idArray.forEach((id) => {
      if (id) {
        const selected = !isSelected(newSelectionState, id);
        newSelectionState = updateSelectionState(
          newSelectionState,
          id,
          selected,
          categorizedData,
        );
      }
    });

    if (shouldUpdateLatestSavedSelectionState)
      setLatestSavedSelectionState(newSelectionState);
    setSelectionState(newSelectionState);
  };

  const isChecked = (id: string, useSemiSelected = false) => {
    if (!useSemiSelected) {
      return isSelected(selectionState, id);
    } else {
      return (
        isSelected(selectionState, id) || isSemiSelected(selectionState, id)
      );
    }
  };

  const handleTopicSubmit = (
    currentSelectionState?: ISelectionState,
    shouldUpdate = true,
  ): ITaxonomyTags => {
    const currentSelections = currentSelectionState || selectionState;

    const selectedTags = buildSelectedTags(currentSelections, categorizedData);
    const taxonomyTags = buildTaxonomyTags(selectedTags);

    if (shouldUpdate) setSelectedTaxonomyTags(taxonomyTags);
    return taxonomyTags;
  };

  useEffect(() => {
    if (taxonomyTagsSlugs.length === 0) {
      setSelectionState({});
      setLatestSavedSelectionState({});
    }
  }, [taxonomyTagsSlugs]);

  useEffect(() => {
    if (shouldUpdateTags) {
      handleTopicSubmit(selectionState);
      setShouldUpdateTags(false);
    }
  }, [shouldUpdateTags, selectionState]);

  useEffect(() => {
    setPrevTaxonomyQueryValue(taxonomyTagsSlugs);
  }, [taxonomyTagsSlugs]);

  useEffect(() => {
    const taxonomyTagsSlugs = collectTaxonomySlugs(selectedTaxonomyTags);
    if (taxonomyTagsSlugs.length === 0) {
      setSelectionState({});
      setLatestSavedSelectionState({});
      setTaxonomyTagsSlugs([]);
      deleteSearchParamValues(AgathaSchemaParamsEnum.taxonomy_tags);
    } else {
      const differenceInQuery = getUniqueElements(
        prevTaxonomyQueryValue,
        taxonomyTagsSlugs,
      );
      const idsToToggle = getIdsFromDifferenceInQuery(
        differenceInQuery,
        categorizedData,
      );
      if (idsToToggle.length > 0) {
        let heighestOrderId;
        for (let i = 1; i < Object.keys(categorizedData).length - 1; i++) {
          const currentLevelData = categorizedData[`level${i}`];
          if (currentLevelData && currentLevelData.length > 0) {
            const heighestOrderTag = currentLevelData.filter((tag) => {
              return idsToToggle.includes(tag.id);
            });
            if (heighestOrderTag.length > 0) {
              heighestOrderId = heighestOrderTag[0]?.id;
              break;
            }
          }
        }
        if (
          heighestOrderId &&
          prevTaxonomyQueryValue.length > taxonomyTagsSlugs.length &&
          hasSearchParamValue(controlRemoveTaxonomyTag, "true")
        ) {
          toggleSelection(heighestOrderId, true);
          deleteSearchParamValues(controlRemoveTaxonomyTag);
        }
        setTaxonomyTagsSlugs(taxonomyTagsSlugs);
        if (taxonomyTagsSlugs.length > 0) {
          setSearchParamValue(
            AgathaSchemaParamsEnum.taxonomy_tags,
            taxonomyTagsSlugs.join(","),
          );
        } else {
          deleteSearchParamValues(AgathaSchemaParamsEnum.taxonomy_tags);
        }
      }
      setPrevTaxonomyQueryValue(taxonomyTagsSlugs);
    }
    deleteSearchParamValues(controlRemoveTaxonomyTag);
  }, [selectedTaxonomyTags]);

  return (
    <TaxonomyTagsContext.Provider
      value={{
        selectionState,
        toggleSelection,
        categorizedData,
        searchQuery,
        setSearchQuery,
        top5Ela,
        isChecked,
        handleTopicSubmit,
        setSelectionState,
        isTabletOrMobileView: currentBreakpointRange === `0,${MD_WIDTH}`,
        prevTaxonomyQueryValue,
        setPrevTaxonomyQueryValue,
        setShouldUpdateTags,
        latestSavedSelectionState,
        setLatestSavedSelectionState,
      }}
    >
      {children}
    </TaxonomyTagsContext.Provider>
  );
};

export const useTaxonomyTagsContext = (): ITaxonomyTagsContextProps =>
  useContext(TaxonomyTagsContext);

TaxonomyTagsProvider.displayName = "TaxonomyTagsProvider";
